import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

const SCORE_KEYS = [
  ["story_health", "Story Health"],
  ["structure", "Structure"],
  ["character", "Character"],
  ["dialogue", "Dialogue"],
  ["pacing", "Pacing"],
  ["conflict", "Conflict"],
  ["commercial_potential", "Commercial"],
  ["production_complexity", "Production"],
];

export default function AIStudio() {
  const { projectId } = useParams();
  const [report, setReport] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const { updateCredits } = useAuth();

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/ai/script-doctor/latest/${projectId}`);
        if (data?.data) setReport(data.data);
    } catch (err) {
      logger.warn("dashboard: could not load latest doctor report", err);
    }
    })();
  }, [projectId]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/script-doctor", { project_id: projectId });
      setReport(data.report);
      updateCredits(data.credits);
      toast.success("Analysis complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Script Doctor</div>
            <h1 className="font-editorial text-4xl">Story diagnostics</h1>
          </div>
          <button onClick={run} disabled={busy}
            className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 text-sm font-medium hover:bg-[#F59E0B] inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
            data-testid="doctor-run">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {report ? "Re-analyse" : "Analyse screenplay"}
          </button>
        </div>

        {!report ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <div className="font-editorial text-2xl">No analysis yet.</div>
            <p className="text-sm text-[#A1A1AA] mt-2 max-w-md mx-auto">
              Run the Script Doctor to see structural, character, pacing and commercial diagnostics — with actionable notes tied to real scenes.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E1E1E] mb-10">
              {SCORE_KEYS.map(([k, label]) => (
                <div key={k} className="bg-[#0A0A0A] p-5" data-testid={`doctor-score-${k}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <div className={`font-editorial text-5xl ${scoreColor(report.scores?.[k])}`}>
                      {report.scores?.[k] ?? 0}
                    </div>
                    <div className="text-xs text-[#71717A]">/100</div>
                  </div>
                  <div className="mt-2 h-1 bg-[#1E1E1E]">
                    <div className="h-full bg-[#D4AF37]" style={{ width: `${report.scores?.[k] ?? 0}%` }}></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="md:col-span-2 border border-[#1E1E1E] bg-[#141414] p-6">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Summary</div>
                <div className="text-sm text-[#E4E4E7] leading-relaxed">{report.summary}</div>
              </div>
              <div className="border border-[#1E1E1E] bg-[#141414] p-6">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Strengths</div>
                <ul className="space-y-2 text-sm text-[#E4E4E7]">
                  {(report.strengths || []).map((s, i) => (
                    <li key={`strength-${i}-${s.slice(0,20)}`} className="flex gap-2"><span className="text-[#D4AF37]">→</span>{s}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Issues</div>
            <div className="space-y-3">
              {(report.issues || []).map((iss, i) => (
                <div key={iss.id || `issue-${i}-${(iss.title || '').slice(0,24)}`} className="border border-[#1E1E1E] bg-[#141414] p-5" data-testid={`doctor-issue-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className={`shrink-0 w-9 h-9 border flex items-center justify-center ${severityBorder(iss.severity)}`}>
                      <AlertTriangle size={14} className={severityText(iss.severity)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-4">
                        <div className="font-editorial text-xl">{iss.title}</div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                          <span className={severityText(iss.severity)}>{iss.severity}</span>
                          <span className="text-[#71717A]">/</span>
                          <span className="text-[#A1A1AA]">{iss.category}</span>
                        </div>
                      </div>
                      {iss.affected_scene && <div className="text-xs text-[#71717A] font-mono-editor mt-1">{iss.affected_scene}</div>}
                      <div className="text-sm text-[#E4E4E7] mt-2 leading-relaxed">{iss.explanation}</div>
                      {iss.evidence && <div className="text-xs text-[#A1A1AA] italic mt-2 border-l-2 border-[#27272A] pl-3">{iss.evidence}</div>}
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <div className="text-[#A1A1AA]"><span className="text-[#D4AF37]">Recommendation:</span> {iss.recommendation}</div>
                        <Link to={`/app/projects/${projectId}/script`}
                          className="border border-[#D4AF37] text-[#D4AF37] px-3 py-1 uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
                          Fix with AI →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function scoreColor(v) {
  if (v == null) return "text-[#71717A]";
  if (v >= 75) return "text-[#D4AF37]";
  if (v >= 55) return "text-[#F59E0B]";
  return "text-[#7F1D1D]";
}
function severityText(s) {
  return { high: "text-[#7F1D1D]", medium: "text-[#F59E0B]", low: "text-[#A1A1AA]" }[s] || "text-[#A1A1AA]";
}
function severityBorder(s) {
  return { high: "border-[#7F1D1D]", medium: "border-[#F59E0B]", low: "border-[#27272A]" }[s] || "border-[#27272A]";
}
